/**
 * Contracts — table view of every DocuSign envelope the user has fired.
 * Live status badges, download signed PDF, void, and (in mock mode) a
 * "march status forward" button for dev verification.
 */
import { useCallback, useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { FileSignature, Plus, Download, RefreshCw, FastForward, AlertTriangle, X, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import SendForSignatureModal from "@/components/SendForSignatureModal";
import GoLiveBatchCard from "@/components/GoLiveBatchCard";

const STATUS_STYLES = {
  sent:       "bg-blue-100 text-blue-800 border-blue-200",
  delivered:  "bg-amber-100 text-amber-800 border-amber-200",
  completed:  "bg-emerald-100 text-emerald-800 border-emerald-200",
  voided:     "bg-zinc-200 text-zinc-700 border-zinc-300",
  declined:   "bg-rose-100 text-rose-800 border-rose-200",
  failed:     "bg-rose-100 text-rose-800 border-rose-200",
};

function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || "bg-zinc-100 text-zinc-700 border-zinc-200";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wider border ${cls}`}
      data-testid={`envelope-status-${status}`}
    >
      {status}
    </span>
  );
}

export default function Contracts() {
  const [envelopes, setEnvelopes] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const reload = useCallback(async ({ withSpinner = false } = {}) => {
    if (withSpinner) setLoading(true);
    try {
      const [list, st] = await Promise.all([
        api.get("/docusign/envelopes?limit=200"),
        api.get("/docusign/status"),
      ]);
      setEnvelopes(list.data?.envelopes || []);
      setStatus(st.data);
    } catch (e) {
      toast.error("Failed to load contracts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const advance = async (id) => {
    try {
      const { data } = await api.post(`/docusign/envelopes/${id}/mock-advance`);
      toast.success(`Status → ${data.status}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to advance");
    }
  };

  const refresh = async (id) => {
    try {
      const { data } = await api.post(`/docusign/envelopes/${id}/refresh`);
      toast.success(`Status: ${data.status}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    }
  };

  const voidEnv = async (id) => {
    if (!window.confirm("Void this envelope? This cannot be undone.")) return;
    try {
      await api.post(`/docusign/envelopes/${id}/void`, { reason: "Voided by sender" });
      toast.success("Envelope voided");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Void failed");
    }
  };

  const download = (id) => {
    const token = localStorage.getItem("af_token") || "";
    // Open in a new tab so the browser handles the PDF download.
    const url = `${API}/docusign/envelopes/${id}/document`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error("Download failed");
        return r.blob();
      })
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = `${id}.pdf`;
        a.click();
        URL.revokeObjectURL(u);
      })
      .catch((e) => toast.error(e.message));
  };

  return (
    <div data-testid="contracts-page">
      <div className="topbar">
        <div>
          <div className="crumb">Contracts</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">
            E-Signatures
          </h1>
          <div className="text-sm text-[var(--ink-2)] mt-1">
            Send assignment contracts, purchase agreements, and JV splits through DocuSign.
          </div>
        </div>
        <Button
          onClick={() => setModalOpen(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="new-envelope-btn"
        >
          <Plus size={16} className="mr-2" />
          New Envelope
        </Button>
      </div>

      {/* Per-user OAuth prompt: platform DocuSign is live, but this operator
          hasn't connected their own account yet. Without connecting, they
          can't send envelopes (backend gates non-admins for cap safety). */}
      {status?.configured && !status?.mock_mode && !status?.user_connected && (
        <div
          className="mb-4 flex items-start gap-3 p-4 rounded-xl border-2 border-indigo-300 bg-gradient-to-r from-indigo-50 to-purple-50 shadow-sm"
          data-testid="docusign-connect-banner"
        >
          <Link2 size={22} className="mt-0.5 shrink-0 text-indigo-600" />
          <div className="flex-1">
            <div className="font-semibold text-indigo-950 text-base">
              Connect your DocuSign account to send contracts
            </div>
            <div className="text-sm text-indigo-900/80 mt-1 leading-relaxed">
              iSpeedToClose routes envelopes through <strong>your own</strong> DocuSign so
              you keep full control over quota, branding, and legal audit trails.
              One-click connect via secure OAuth — no credentials shared.
            </div>
            <Link
              to="/v3/settings"
              className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors"
              data-testid="docusign-connect-cta"
            >
              <Link2 size={14} />
              Connect DocuSign →
            </Link>
          </div>
        </div>
      )}

      {status?.mock_mode && (
        <div
          className="mb-4 flex items-start gap-3 p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900"
          data-testid="docusign-mock-banner"
        >
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div className="text-sm">
            <strong>Mock mode active.</strong> DocuSign credentials aren&apos;t set yet — envelopes will
            be created with fake IDs and signed PDFs will be stubs. Add your DocuSign Integration
            Key, User ID, Account ID, and RSA private key to <code className="font-mono text-xs px-1 py-0.5 bg-amber-100 rounded">/app/backend/.env</code>
            {" "}to send real envelopes.
          </div>
        </div>
      )}

      {/* Go-Live batch: shown ONLY when DocuSign JWT is configured (i.e. we
          can actually send real envelopes) AND the caller is admin. It's
          the fastest way to clear DocuSign's 20-envelope threshold before
          submitting the "Go Live" promotion request. */}
      {status?.configured && !status?.mock_mode && (
        <GoLiveBatchCard />
      )}

      <div className="card overflow-hidden">
        <table className="w-full text-sm" data-testid="envelopes-table">
          <thead className="bg-[var(--surface-2)] text-[var(--ink-2)]">
            <tr>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">Template</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">Recipients</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">Status</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">Sent</th>
              <th className="text-right px-4 py-2 text-xs uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-[var(--ink-3)]">Loading…</td></tr>
            )}
            {!loading && envelopes.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-[var(--ink-3)]">
                  <FileSignature size={32} className="mx-auto mb-3 opacity-40" />
                  <div>No envelopes yet — fire your first one from a Pipeline card or click <strong>New Envelope</strong> above.</div>
                </td>
              </tr>
            )}
            {envelopes.map((e) => (
              <tr key={e.id} className="border-t border-[var(--line)]" data-testid={`envelope-row-${e.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold">{e.template_label}</div>
                  <div className="text-xs text-[var(--ink-3)] mt-0.5">{e.subject}</div>
                  {e.is_mock && (
                    <span className="inline-block mt-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                      mock
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">
                  {(e.recipients || []).map((r, i) => (
                    <div key={`${r.email || 'unknown'}-${i}`}>
                      <span className="font-medium">{r.name}</span>
                      <span className="text-[var(--ink-3)]"> · {r.email}</span>
                    </div>
                  ))}
                </td>
                <td className="px-4 py-3"><StatusPill status={e.status} /></td>
                <td className="px-4 py-3 text-xs text-[var(--ink-2)]">
                  {e.sent_at ? new Date(e.sent_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3 text-right space-x-1">
                  {e.is_mock && e.status !== "completed" && e.status !== "voided" && (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => advance(e.id)}
                      data-testid={`envelope-advance-${e.id}`}
                      title="Mock-mode only: march to next status"
                    >
                      <FastForward size={14} />
                    </Button>
                  )}
                  {!e.is_mock && e.status !== "completed" && e.status !== "voided" && (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => refresh(e.id)}
                      data-testid={`envelope-refresh-${e.id}`}
                      title="Refresh status from DocuSign"
                    >
                      <RefreshCw size={14} />
                    </Button>
                  )}
                  {e.status === "completed" && (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => download(e.id)}
                      data-testid={`envelope-download-${e.id}`}
                    >
                      <Download size={14} className="mr-1" /> PDF
                    </Button>
                  )}
                  {e.status !== "completed" && e.status !== "voided" && (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => voidEnv(e.id)}
                      data-testid={`envelope-void-${e.id}`}
                      className="text-rose-600 border-rose-200 hover:bg-rose-50"
                    >
                      <X size={14} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SendForSignatureModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        lead={null}
        onSent={() => reload()}
      />
    </div>
  );
}
